import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import {
  deleteTrackOverwrite,
  getTrackOverwrite,
  upsertTrackOverwrite,
} from '~/modules/label-perf/api/overwrites';
import { Input } from '~/modules/label-perf/components/Input';
import { TextArea } from '~/modules/label-perf/components/TextArea';

export interface TrackOverwriteProps {
  isrc: string;
  className?: string;
  closeHandler?: () => void;
}

interface FormData {
  trackTitle?: string;
  artistName?: string;
  comment?: string;
}

interface Authorship {
  createdBy: string;
  createdAt: Date;
  updatedBy: string;
  updatedAt: Date;
}

const TrackOverwrite = (props: TrackOverwriteProps): JSX.Element => {
  const { t } = useTranslation('label-perf');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [authorship, setAuthorship] = useState<Authorship | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getTrackOverwrite(props.isrc)
      .then((data) => {
        if (data) {
          setFormData({
            trackTitle: data.track_title,
            artistName: data.artist_name,
            comment: data.comment,
          });
          setAuthorship({
            createdBy: data.created_by.name,
            createdAt: new Date(data.created_at),
            updatedBy: data.updated_by.name,
            updatedAt: new Date(data.updated_at),
          });
        }
        setLoading(false);
        return false;
      })
      .catch((_error) => {
        setError(
          `Failed to fetch track overwrite data: ${(_error as Error).message}`,
        );
        setLoading(false);
      });
  }, [props.isrc, reload]);

  const handleInputChange =
    (field: keyof FormData): ((value: string) => void) =>
    (value: string): void => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleOnSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await upsertTrackOverwrite({
        isrc: props.isrc,
        track_title: formData?.trackTitle,
        artist_name: formData?.artistName,
        comment: formData?.comment,
      });
      setReload(new Date().toISOString());
    } catch (_error) {
      setError(
        `Failed to save track overwrite data: ${(_error as Error).message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setAuthorship(null);
    setSaving(true);
    setError(null);
    try {
      await deleteTrackOverwrite(props.isrc);
    } catch (_error) {
      setError(
        `Failed to delete track overwrite data: ${(_error as Error).message}`,
      );
    } finally {
      setSaving(false);
      props.closeHandler?.();
    }
  };

  return (
    <div className={props.className}>
      <div className="mb-sm">
        <p
          className={cn(
            'mb-md flex items-center text-body-md font-semibold',
            props.className,
          )}
        >
          {t('ISRC')} - {props.isrc}
        </p>
      </div>
      {loading ? (
        <Icon
          type="spinner"
          variant="solid"
          size="sm"
          className="fa-spin mb-sm"
        />
      ) : null}
      {!loading && (
        <>
          <div className="mb-md">
            <p
              className={cn(
                'mb-sm flex items-center text-body-sm font-semibold',
                props.className,
              )}
            >
              {t('body.trackTitle')}
            </p>
            <Input
              isReadonly={loading}
              onChange={handleInputChange('trackTitle')}
              placeholder="Track Title, empty value means no overwrrite"
              value={formData?.trackTitle || ''}
            />
          </div>
          <div className="mb-md">
            <p
              className={cn(
                'mb-sm flex items-center text-body-sm font-semibold',
                props.className,
              )}
            >
              {t('body.artistName')}
            </p>
            <Input
              isReadonly={loading}
              onChange={handleInputChange('artistName')}
              placeholder="Artist Name, empty value means no overwrrite"
              value={formData?.artistName || ''}
            />
          </div>
          <div className="mb-md">
            <p
              className={cn(
                'mb-sm flex items-center text-body-sm font-semibold',
                props.className,
              )}
            >
              {t('Comment')}
            </p>
            <TextArea
              isReadonly={loading}
              onChange={handleInputChange('comment')}
              placeholder="Describe why overwrite is done"
              value={formData?.comment || ''}
            />
          </div>
        </>
      )}
      {!loading && authorship ? (
        <div className="mb-sm text-body-sm text-subtle">
          <div>
            {t('body.createdBy')} {authorship.createdBy} {t('body.at')}{' '}
            {authorship.createdAt.toLocaleDateString()}{' '}
            {authorship.createdAt.toLocaleTimeString()}
          </div>
          <div>
            {t('table.modifiedBy')} {authorship.updatedBy} {t('body.at')}{' '}
            {authorship.updatedAt.toLocaleDateString()}{' '}
            {authorship.updatedAt.toLocaleTimeString()}
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="mb-sm text-body-sm text-error-default">
          <p>{error}</p>
        </div>
      ) : null}
      {!loading && (
        <div className="mb-md flex justify-end border-b py-md border-emphasis-subtle">
          <button
            className={cn(
              'mr-sm rounded-md border px-md py-sm text-body-sm font-semibold border-emphasis-subtle text-white hover:bg-primary-default',
              saving ? 'cursor-not-allowed' : '',
            )}
            disabled={saving}
            onClick={() => handleOnSave()}
            type="button"
          >
            {saving ? t('actions.saving') : t('actions.save')}
          </button>
          {authorship ? (
            <button
              className="rounded-md border px-md py-sm text-body-sm font-semibold bg-error-default border-emphasis-subtle text-white hover:bg-error-hover"
              onClick={() => handleDelete()}
              type="button"
            >
              {t('actions.delete')}
            </button>
          ) : null}
        </div>
      )}

      <div className="my-md text-body-sm text-subtle">
        <p className="mb-xs font-semibold">{t('body.notes')}</p>
        <p className="mb-sm">{t('desc.overwrite')}</p>
        <p className="mb-sm">{t('desc.overwritenData')}</p>
        <p className="mb-sm">{t('desc.overwriteTakeEffect')}</p>
        <p className="mb-sm">{t('desc.provideOverwrite')}</p>
      </div>
    </div>
  );
};

TrackOverwrite.displayName = 'TrackOverwrite';

export { TrackOverwrite };
